/**
 * 紙の描き手の入口（#217）。版 2（engine.ts）＝見積書・請求書の紙（プリセットも自分の書式も）。版 1（render.ts）＝#217 より前に出した
 * 自分の書式の紙と、領収書の自分の書式（領収書は #217 の対象外＝今のまま）。API の PDF もエディタも、ここを通して同じ版で描く
 */
import type { PaperData, PaperLayout } from "@genbaryoku/contract";
import { fieldOverflows, renderPages2, renderPaperHtml2, type Geometry, type Render2Options } from "./engine.js";
import { elBox, renderPagesV1, renderPaperHtmlV1, tableHeight, type RenderOptions } from "./render.js";

export type Engine = 1 | 2;
export type PaperRenderOptions = RenderOptions & Render2Options & { engine?: Engine };

/** 描き手の版：決めていなければ 領収書は 1、見積書・請求書は 2 */
export const engineFor = (layout: Pick<PaperLayout, "kind">, engine?: Engine): Engine => engine ?? (layout.kind === "receipt" ? 1 : 2);

export function renderPaperHtml(layout: PaperLayout, data: PaperData, opts: PaperRenderOptions = {}): string {
  return engineFor(layout, opts.engine) === 1 ? renderPaperHtmlV1(layout, data, v1opts(opts)) : renderPaperHtml2(layout, data, opts);
}

/** ページごとの中身（.page の中の HTML）。エディタは1ページ目だけ受け取って自分の枠に入れる */
export function renderPages(layout: PaperLayout, data: PaperData, opts: PaperRenderOptions = {}): { pages: string[] } {
  return engineFor(layout, opts.engine) === 1 ? renderPagesV1(layout, data, v1opts(opts)) : renderPages2(layout, data, opts);
}

const v1opts = (o: PaperRenderOptions): RenderOptions => ({ ...(o.mode ? { mode: o.mode } : {}), ...(o.assets ? { assets: o.assets } : {}), ...(o.labels ? { labels: o.labels } : {}) });

/** 1ページ目の部品と表の位置（エディタ：選択・枠・当たり判定・注意）。版 1 は書式の位置そのもの */
export function paperGeometry(layout: PaperLayout, data: PaperData, opts: PaperRenderOptions = {}): Geometry {
  if (engineFor(layout, opts.engine) === 2) return renderPages2(layout, data, { ...opts, mode: "edit" }).geom;
  const t = layout.table;
  const els: Geometry["els"] = {};
  for (const e of layout.els) els[e.id] = elBox(e, t);
  return {
    els,
    table: { x: t.x, y: t.y, w: t.w, h: tableHeight(t) },
    cols: t.cols.filter((c) => c.on).sort((a, b) => a.x - b.x).map((c) => ({ id: c.id, label: c.label, x: t.x + c.x, w: c.w })),
    dockBase: t.y + tableHeight(t) + 3,
    boxes: [],
    overflow: fieldOverflows(layout, data),
  };
}
